import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSupportContext } from '../../context/SupportContext';
import axios from 'axios';
import {
  Users,
  Search,
  RefreshCw,
  MoreHorizontal,
  Mail,
  KeyRound,
  Copy,
  Building2,
  Eye,
  Pencil,
  ShieldCheck,
  ShieldOff,
  UserX,
  UserCheck,
  Fingerprint,
  Trash2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { toast } from 'sonner';
import { formatDateTimeWithTZ } from '../../utils/timezone';
import EditUserDrawer from '../../components/platform/EditUserDrawer';
import { openUserSupportProfile, truncateUserId } from '../../utils/platformUserSupport';
import { isPlatformConsoleRole } from '../../utils/hostnamePortal';

const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const ROLE_OPTIONS = [
  { value: 'all', label: 'All roles' },
  { value: 'platform_owner', label: 'Platform owner' },
  { value: 'platform_admin', label: 'Platform admin' },
  { value: 'owner', label: 'Agency owner' },
  { value: 'agency_owner', label: 'Agency owner (alt)' },
  { value: 'specialist', label: 'Specialist' },
  { value: 'staff', label: 'Staff' },
  { value: 'self_repair_customer', label: 'Self-repair' },
  { value: 'managed_client', label: 'Managed client' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'archived', label: 'Archived' },
];

const PORTAL_OPTIONS = [
  { value: 'all', label: 'All portals' },
  { value: 'platform_portal', label: 'Platform' },
  { value: 'staff_portal', label: 'Staff' },
  { value: 'self_serve_portal', label: 'Self-serve' },
  { value: 'managed_client_portal', label: 'Managed client' },
];

const VERIFIED_OPTIONS = [
  { value: 'all', label: 'Any verification' },
  { value: 'true', label: 'Verified' },
  { value: 'false', label: 'Unverified' },
];

function statusBadgeVariant(status) {
  if (status === 'active') return 'default';
  if (status === 'suspended' || status === 'archived') return 'destructive';
  return 'secondary';
}

function formatDetail(value) {
  if (!value) return '—';
  try {
    return formatDateTimeWithTZ(value);
  } catch {
    return String(value);
  }
}

function isUserDisabled(user) {
  return user.is_active === false || user.status === 'inactive' || user.status === 'archived';
}

function CopyableUserId({ userId, onCopy }) {
  if (!userId) return <span className="text-muted-foreground">—</span>;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => onCopy(userId, 'User ID copied')}
            title={userId}
            className="font-mono text-xs bg-muted hover:bg-muted/80 px-2 py-0.5 rounded max-w-[140px] truncate inline-block text-left"
          >
            {truncateUserId(userId)}
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <p className="font-mono text-xs">{userId}</p>
          <p className="text-xs text-muted-foreground mt-1">Click to copy</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

const PlatformUsersPage = () => {
  const { token, user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { enterClientSupport, enterAgencyContext, isPlatformOwner } = useSupportContext();

  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionUserId, setActionUserId] = useState(null);

  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const roleFilter = searchParams.get('role') || 'all';
  const agencyFilter = searchParams.get('agency_id') || 'all';
  const portalFilter = searchParams.get('portal_type') || 'all';
  const statusFilter = searchParams.get('status') || 'all';
  const verifiedFilter = searchParams.get('email_verified') || 'all';
  const page = Number(searchParams.get('page') || '1');

  const [editUser, setEditUser] = useState(null);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [deleteNowOpen, setDeleteNowOpen] = useState(false);
  const [deleteNowTarget, setDeleteNowTarget] = useState(null);
  const [deleteNowName, setDeleteNowName] = useState('');
  const [deleteNowCode, setDeleteNowCode] = useState('');
  const [deleteNowError, setDeleteNowError] = useState('');
  const [deleteNowLoading, setDeleteNowLoading] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };
  const canUseSupportContext = Boolean(isPlatformOwner);
  const currentUserId = currentUser?.user_id;

  const updateFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === 'all') {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    if (key !== 'page') next.delete('page');
    setSearchParams(next);
  };

  const fetchAgencies = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/owner-console/agencies?limit=200`, {
        headers: authHeaders,
      });
      setAgencies(response.data.items || []);
    } catch {
      setAgencies([]);
    }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', '50');
      if (searchParams.get('q')) params.set('q', searchParams.get('q'));
      if (roleFilter !== 'all') params.set('role', roleFilter);
      if (agencyFilter !== 'all') params.set('agency_id', agencyFilter);
      if (portalFilter !== 'all') params.set('portal_type', portalFilter);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (verifiedFilter !== 'all') params.set('email_verified', verifiedFilter);

      const response = await axios.get(`${API}/platform/users?${params}`, { headers: authHeaders });
      setUsers(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(', ')
        : detail || 'Failed to load users';
      toast.error(message);
      setUsers([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [token, page, roleFilter, agencyFilter, portalFilter, statusFilter, verifiedFilter, searchParams]);

  useEffect(() => {
    fetchAgencies();
  }, [fetchAgencies]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (searchTerm.trim()) {
      next.set('q', searchTerm.trim());
    } else {
      next.delete('q');
    }
    next.delete('page');
    setSearchParams(next);
  };

  const copyText = async (text, successMessage) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(successMessage);
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };

  const fetchUserDetail = async (userId) => {
    const response = await axios.get(`${API}/platform/users/${userId}`, { headers: authHeaders });
    return response.data;
  };

  const openEditUser = async (user) => {
    setActionUserId(user.user_id);
    setActionLoading(true);
    try {
      const detail = await fetchUserDetail(user.user_id);
      setEditUser(detail);
      setEditDrawerOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load user details');
    } finally {
      setActionLoading(false);
      setActionUserId(null);
    }
  };

  const handleViewProfile = async (user) => {
    setActionUserId(user.user_id);
    setActionLoading(true);
    try {
      const result = await openUserSupportProfile(user, {
        navigate,
        enterClientSupport,
        enterAgencyContext,
        canUseSupportContext,
        currentUserId,
      });
      if (result.action === 'edit') {
        await openEditUser(user);
      } else {
        toast.success(`Opened support profile for ${user.name || user.email}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to open profile');
    } finally {
      setActionLoading(false);
      setActionUserId(null);
    }
  };

  const openLinkedAgency = (user) => {
    if (!user.agency_id) return;
    navigate(`/platform/agencies/${user.agency_id}`);
  };

  const openResetConfirm = (user) => {
    setResetTarget(user);
    setResetDialogOpen(true);
  };

  const sendPasswordReset = async () => {
    if (!resetTarget?.user_id) return;
    setActionLoading(true);
    setActionUserId(resetTarget.user_id);
    try {
      const response = await axios.post(
        `${API}/platform/users/${resetTarget.user_id}/send-password-reset`,
        {},
        { headers: authHeaders }
      );
      const email = response.data.email || resetTarget.email;
      toast.success(`Password reset link sent to ${email}`);
      setResetDialogOpen(false);
      setResetTarget(null);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(', ')
        : detail || 'Failed to send password reset link';
      toast.error(message);
    } finally {
      setActionLoading(false);
      setActionUserId(null);
    }
  };

  const resendVerification = async (user) => {
    setActionLoading(true);
    setActionUserId(user.user_id);
    try {
      const response = await axios.post(
        `${API}/platform/users/${user.user_id}/resend-verification`,
        {},
        { headers: authHeaders }
      );
      toast.success(response.data.message || 'Verification email sent');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || 'Failed to resend verification');
    } finally {
      setActionLoading(false);
      setActionUserId(null);
    }
  };

  const openStatusConfirm = (user, enable) => {
    if (!enable && user.user_id === currentUserId) {
      toast.error('You cannot disable your own account.');
      return;
    }
    setStatusTarget({ user, enable });
    setStatusDialogOpen(true);
  };

  const confirmStatusChange = async () => {
    if (!statusTarget?.user) return;
    const { user, enable } = statusTarget;
    setActionLoading(true);
    setActionUserId(user.user_id);
    try {
      const path = enable ? 'enable' : 'disable';
      await axios.post(`${API}/platform/users/${user.user_id}/${path}`, {}, { headers: authHeaders });
      toast.success(enable ? 'User enabled' : 'User disabled');
      setStatusDialogOpen(false);
      setStatusTarget(null);
      await fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Status update failed');
    } finally {
      setActionLoading(false);
      setActionUserId(null);
    }
  };

  const openDeleteNow = (user) => {
    if (user.user_id === currentUserId) {
      toast.error('You cannot delete your own account.');
      return;
    }
    setDeleteNowTarget(user);
    setDeleteNowName('');
    setDeleteNowCode('');
    setDeleteNowError('');
    setDeleteNowOpen(true);
  };

  const confirmDeleteNow = async () => {
    if (!deleteNowTarget) return;
    setDeleteNowLoading(true);
    setDeleteNowError('');
    try {
      await axios.post(
        `${API}/owner-console/users/${deleteNowTarget.user_id}/delete-now`,
        {
          two_fa_code: deleteNowCode,
          confirmation_name: deleteNowName,
        },
        { headers: authHeaders }
      );
      toast.success('User deleted');
      setDeleteNowOpen(false);
      setDeleteNowTarget(null);
      await fetchUsers();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message =
        (typeof detail === 'object' && (detail.message || detail.error))
        || (typeof detail === 'string' ? detail : null)
        || 'Failed to delete user';
      const code = typeof detail === 'object' ? detail.code : null;
      setDeleteNowError(message);
      toast.error(message);
      if (code === 'TWO_FACTOR_NOT_CONFIGURED') {
        setDeleteNowError(
          `${message} Set up 2FA in Platform Settings > Security > Authentication.`
        );
      }
    } finally {
      setDeleteNowLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / 50));
  const isSelf = (user) => user.user_id === currentUserId;

  const renderActionsMenu = (user) => {
    const rowLoading = actionLoading && actionUserId === user.user_id;
    const disabled = isUserDisabled(user);

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8" disabled={rowLoading}>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuItem onClick={() => handleViewProfile(user)} disabled={rowLoading}>
            <Eye className="h-4 w-4 mr-2" />
            View profile
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => openEditUser(user)} disabled={rowLoading}>
            <Pencil className="h-4 w-4 mr-2" />
            Edit user
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => openResetConfirm(user)} disabled={rowLoading}>
            <KeyRound className="h-4 w-4 mr-2" />
            Send reset link
          </DropdownMenuItem>
          {!user.email_verified && (
            <DropdownMenuItem onClick={() => resendVerification(user)} disabled={rowLoading}>
              <Mail className="h-4 w-4 mr-2" />
              Resend verification
            </DropdownMenuItem>
          )}
          {user.agency_id ? (
            <DropdownMenuItem onClick={() => openLinkedAgency(user)}>
              <Building2 className="h-4 w-4 mr-2" />
              Open linked agency
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => copyText(user.user_id, 'User ID copied')}>
            <Fingerprint className="h-4 w-4 mr-2" />
            Copy user ID
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => copyText(user.email, 'Email copied')}>
            <Copy className="h-4 w-4 mr-2" />
            Copy email
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {!disabled ? (
            <DropdownMenuItem
              onClick={() => openStatusConfirm(user, false)}
              disabled={rowLoading || isSelf(user)}
              title={isSelf(user) ? 'You cannot disable your own account' : undefined}
            >
              <UserX className="h-4 w-4 mr-2" />
              Disable user
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onClick={() => openStatusConfirm(user, true)} disabled={rowLoading}>
              <UserCheck className="h-4 w-4 mr-2" />
              Enable user
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={() => openDeleteNow(user)}
            disabled={rowLoading || isSelf(user)}
            className="text-red-600"
            data-testid="platform-user-delete-now"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete Immediately
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  if (!isPlatformConsoleRole(currentUser?.role)) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        Platform admin access required.
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-[1800px] mx-auto">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Users className="h-6 w-6 text-primary" />
            Platform Users
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            View and manage users across all agencies ({total.toLocaleString()} total)
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchUsers} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <form onSubmit={handleSearch} className="flex flex-1 gap-2">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search user ID, name, email, or phone"
              className="pl-9"
            />
          </div>
          <Button type="submit" variant="secondary">
            Search
          </Button>
        </form>

        <div className="flex flex-wrap gap-2">
          <Select value={roleFilter} onValueChange={(v) => updateFilter('role', v)}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              {ROLE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={agencyFilter} onValueChange={(v) => updateFilter('agency_id', v)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Agency" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All agencies</SelectItem>
              {agencies.map((agency) => (
                <SelectItem key={agency.agency_id || agency.id} value={agency.agency_id || agency.id}>
                  {agency.name || agency.agency_name || agency.agency_id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={portalFilter} onValueChange={(v) => updateFilter('portal_type', v)}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Portal" />
            </SelectTrigger>
            <SelectContent>
              {PORTAL_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={(v) => updateFilter('status', v)}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={verifiedFilter} onValueChange={(v) => updateFilter('email_verified', v)}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Email verified" />
            </SelectTrigger>
            <SelectContent>
              {VERIFIED_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-lg border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b">
              <tr>
                <th className="text-left font-medium px-4 py-3">User ID</th>
                <th className="text-left font-medium px-4 py-3">Name</th>
                <th className="text-left font-medium px-4 py-3">Email</th>
                <th className="text-left font-medium px-4 py-3 hidden lg:table-cell">Phone</th>
                <th className="text-left font-medium px-4 py-3">Role</th>
                <th className="text-left font-medium px-4 py-3 hidden md:table-cell">Portal</th>
                <th className="text-left font-medium px-4 py-3 hidden xl:table-cell">Agency</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="text-left font-medium px-4 py-3 hidden lg:table-cell">Email Verified</th>
                <th className="text-left font-medium px-4 py-3 hidden xl:table-cell">Last Login</th>
                <th className="text-left font-medium px-4 py-3 hidden xl:table-cell">Created</th>
                <th className="text-right font-medium px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={12} className="px-4 py-12 text-center text-muted-foreground">
                    Loading users…
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={12} className="px-4 py-12 text-center text-muted-foreground">
                    No users match your filters.
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.user_id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="px-4 py-3">
                      <CopyableUserId userId={user.user_id} onCopy={copyText} />
                    </td>
                    <td className="px-4 py-3 font-medium">{user.name || '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">{user.email}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground">{user.phone || '—'}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className="font-normal">
                        {user.role}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-muted-foreground text-xs">
                      {user.portal_type || '—'}
                    </td>
                    <td className="px-4 py-3 hidden xl:table-cell">
                      {user.agency_id ? (
                        <Link
                          to={`/platform/agencies/${user.agency_id}`}
                          className="text-primary hover:underline inline-flex items-center gap-1"
                        >
                          <Building2 className="h-3 w-3" />
                          {user.agency_name || user.agency_id}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={statusBadgeVariant(user.status)}>{user.status}</Badge>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      {user.email_verified ? (
                        <Badge variant="default" className="gap-1">
                          <ShieldCheck className="h-3 w-3" />
                          Yes
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="gap-1">
                          <ShieldOff className="h-3 w-3" />
                          No
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden xl:table-cell text-muted-foreground text-xs">
                      {formatDetail(user.last_login)}
                    </td>
                    <td className="px-4 py-3 hidden xl:table-cell text-muted-foreground text-xs">
                      {formatDetail(user.created_at)}
                    </td>
                    <td className="px-4 py-3 text-right">{renderActionsMenu(user)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => updateFilter('page', String(page - 1))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => updateFilter('page', String(page + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send password reset link</DialogTitle>
            <DialogDescription>
              Send password reset link to {resetTarget?.email}?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialogOpen(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button onClick={sendPasswordReset} disabled={actionLoading}>
              {actionLoading ? 'Sending…' : 'Send reset link'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {statusTarget?.enable ? 'Enable this user?' : 'Disable this user?'}
            </DialogTitle>
            <DialogDescription>
              {statusTarget?.enable
                ? `Re-enable ${statusTarget?.user?.email}? They will be able to sign in again.`
                : `Disable ${statusTarget?.user?.email}? They will not be able to sign in until re-enabled.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialogOpen(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button
              variant={statusTarget?.enable ? 'default' : 'destructive'}
              onClick={confirmStatusChange}
              disabled={actionLoading}
            >
              {actionLoading ? 'Saving…' : statusTarget?.enable ? 'Enable user' : 'Disable user'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteNowOpen} onOpenChange={setDeleteNowOpen}>
        <DialogContent data-testid="platform-user-delete-now-modal">
          <DialogHeader>
            <DialogTitle>Delete user immediately</DialogTitle>
            <DialogDescription>
              This will archive/delete this user immediately. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-sm font-medium">
                Type name <span className="font-semibold">{deleteNowTarget?.name || deleteNowTarget?.email}</span>
              </label>
              <Input
                className="mt-1.5"
                value={deleteNowName}
                onChange={(e) => setDeleteNowName(e.target.value)}
                data-testid="platform-user-delete-now-name"
              />
            </div>
            <div>
              <label className="text-sm font-medium">2FA code</label>
              <Input
                className="mt-1.5 tracking-widest"
                inputMode="numeric"
                maxLength={6}
                value={deleteNowCode}
                onChange={(e) => setDeleteNowCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                data-testid="platform-user-delete-now-2fa"
              />
            </div>
            {deleteNowError ? (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
                <p>{deleteNowError}</p>
                {String(deleteNowError).includes('2FA') || String(deleteNowError).includes('Two-factor') ? (
                  <Link
                    to="/platform/settings/authentication"
                    className="font-medium underline"
                    data-testid="platform-user-setup-2fa-link"
                  >
                    Set up 2FA
                  </Link>
                ) : null}
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteNowOpen(false)} disabled={deleteNowLoading}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteNow}
              disabled={
                deleteNowLoading
                || deleteNowCode.length !== 6
                || deleteNowName.trim().toLowerCase()
                  !== String(deleteNowTarget?.name || deleteNowTarget?.email || '').trim().toLowerCase()
              }
              data-testid="platform-user-confirm-delete-now"
            >
              {deleteNowLoading ? 'Deleting…' : 'Delete Immediately'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditUserDrawer
        isOpen={editDrawerOpen}
        onClose={() => {
          setEditDrawerOpen(false);
          setEditUser(null);
        }}
        user={editUser}
        agencyId={editUser?.agency_id}
        agencyName={editUser?.agency_name}
        onSave={() => {
          fetchUsers();
          setEditDrawerOpen(false);
          setEditUser(null);
        }}
      />
    </div>
  );
};

export default PlatformUsersPage;
